/**
 * Top-level MLS listing classification for a search result — the `Residential` /
 * `Commercial` / `Land` bucket, as distinct from `MockProperty.propertyType`,
 * which is our own narrower grid vocabulary (`sfh`, `condo`, `mfh`, …).
 *
 * The two answer different questions. `propertyType` drives the filter chips and
 * the "Single Family" line on the card; this drives the pill that tells you at a
 * glance whether you are looking at a house, a strip mall, or raw acreage.
 *
 * The vocabulary mirrors RealEstateAPI's `listing_property_type` and
 * `supabase/functions/_shared/listing-property-type.ts`. All three must stay in
 * step.
 *
 * @see https://developer.realestateapi.com/reference/listing-property-type
 */
export type ListingPropertyType =
  | "RESIDENTIAL"
  | "RESIDENTIAL_LEASE"
  | "RENTAL"
  | "RESIDENTIAL_INCOME"
  | "COMMERCIAL"
  | "COMMERCIAL_FOR_LEASE"
  | "LAND"
  | "FARM"
  | "MANUFACTURED_IN_PARK"
  | "BUSINESS_OPPORTUNITY";

const LISTING_PROPERTY_TYPES: readonly ListingPropertyType[] = [
  "RESIDENTIAL",
  "RESIDENTIAL_LEASE",
  "RENTAL",
  "RESIDENTIAL_INCOME",
  "COMMERCIAL",
  "COMMERCIAL_FOR_LEASE",
  "LAND",
  "FARM",
  "MANUFACTURED_IN_PARK",
  "BUSINESS_OPPORTUNITY",
] as const;

/**
 * Pill labels. Kept short enough to sit inline on a grid card next to the
 * property-type line, so the MLS's longer descriptors are trimmed
 * ("Business Opportunity" → "Business", "Manufactured In Park" → "Manufactured").
 */
export const LISTING_PROPERTY_TYPE_LABELS: Record<ListingPropertyType, string> = {
  RESIDENTIAL: "Residential",
  RESIDENTIAL_LEASE: "Lease",
  RENTAL: "Lease",
  RESIDENTIAL_INCOME: "Residential Income",
  COMMERCIAL: "Commercial",
  COMMERCIAL_FOR_LEASE: "Commercial Lease",
  LAND: "Land",
  FARM: "Farm",
  MANUFACTURED_IN_PARK: "Manufactured",
  BUSINESS_OPPORTUNITY: "Business",
};

/**
 * Pill classes per classification — semantic tokens only, so dark mode comes for
 * free. Deliberately tint-and-border rather than solid fill: the card already
 * carries a solid status badge over the photo, and a second solid pill in the
 * body would fight it. Text colour is left to the component (`text-foreground`)
 * so contrast never depends on a token's own lightness.
 */
export const LISTING_PROPERTY_TYPE_PILL: Record<ListingPropertyType, string> = {
  RESIDENTIAL: "border-border bg-muted/60",
  RESIDENTIAL_LEASE: "border-secondary/40 bg-secondary/15",
  RENTAL: "border-secondary/40 bg-secondary/15",
  RESIDENTIAL_INCOME: "border-primary/40 bg-primary/15",
  COMMERCIAL: "border-warning/40 bg-warning/15",
  COMMERCIAL_FOR_LEASE: "border-warning/40 bg-warning/15",
  LAND: "border-success/40 bg-success/15",
  FARM: "border-success/40 bg-success/15",
  MANUFACTURED_IN_PARK: "border-border bg-muted/60",
  BUSINESS_OPPORTUNITY: "border-secondary/40 bg-secondary/15",
};

/**
 * Coerce whatever the edge function put on the property to a known
 * classification. Returns null rather than guessing — the pill is additive
 * information, so an unreadable value should render nothing instead of
 * mislabelling a commercial building "Residential".
 */
export function normalizeListingPropertyType(
  value: string | null | undefined,
): ListingPropertyType | null {
  const key = (value ?? "").trim().toUpperCase().replace(/[\s\-/]+/g, "_");
  return LISTING_PROPERTY_TYPES.find((type) => type === key) ?? null;
}

/**
 * True for the three classifications that price in monthly rent rather than
 * purchase price. `property-search` already excludes these, so this exists for
 * callers reading a `pipeline_properties` row saved before that filter landed.
 */
export function isLeaseListing(
  type: ListingPropertyType | null | undefined,
): boolean {
  return (
    type === "RESIDENTIAL_LEASE" ||
    type === "RENTAL" ||
    type === "COMMERCIAL_FOR_LEASE"
  );
}
